import datalayerBuilder from '@core-ui/bopis-facet/datalayer/builder';
import {
  BAR_LABEL_TEXT,
  BAR_LABEL_TEXT_CURBSIDE,
  BAR_LABEL_TEXT_IN_STORE_AND_CURBSIDE,
  PICKUP_CURBSIDE_UNAVAILABLE,
  PICKUP_IN_STORE_UNAVAILABLE,
  TOGGLE_DISABLED_TEXT,
  TOGGLE_DISABLED_TEXT_CURBSIDE,
  TOGGLE_ENABLED_TEXT,
  TOGGLE_ENABLED_TEXT_CURBSIDE,
  TOGGLE_SWITCH_LABEL,
  TOGGLE_SWITCH_LABEL_CURBSIDE,
} from './localization-tokens';
import { BanIcon } from './ban-icon';
import { getFontWeight } from '@core-ui/react-stitch';

const getBopisText = (isCurbsidePickup) => {
  if (isCurbsidePickup) {
    return PICKUP_IN_STORE_UNAVAILABLE;
  } else {
    return PICKUP_CURBSIDE_UNAVAILABLE;
  }
};

const getSelectedStore = (bopisData) => {
  return bopisData?.selectedStore ?? null;
};

const isReadyToInitialize = (
  bopisInitialed,
  getBopisInitialed,
  enabled,
  bopisTealiumInitialized
) => {
  return (
    bopisInitialed !== getBopisInitialed && enabled && !bopisTealiumInitialized
  );
};

const storePickupTextOptions = [
  {
    compareFn: (curbsidePickup, inStorePickup) => {
      return curbsidePickup && inStorePickup;
    },
    getDisplayText: (localize) => {
      const inStoreAndCurbside = localize(BAR_LABEL_TEXT_IN_STORE_AND_CURBSIDE);
      return inStoreAndCurbside.split('&').join('&\n');
    },
  },
  {
    compareFn: (curbsidePickup, inStorePickup) => {
      return curbsidePickup;
    },
    getDisplayText: (localize) => {
      return localize(BAR_LABEL_TEXT_CURBSIDE);
    },
  },
];

export const storeAvailabilityIndicator = (
  isCurbsidePickup,
  isInStorePickup,
  localize,
  isBRRedesignEnabled
) => {
  if (isCurbsidePickup === isInStorePickup) return '';
  const displayText = getBopisText(isCurbsidePickup);
  if (!isBRRedesignEnabled)
    return (
      <div className="bopis-bar__store-availabity-indicator">
        <BanIcon />
        {localize(displayText)}
      </div>
    );
};

export const isInStorePickup = (activeFeatureToggles) => {
  return activeFeatureToggles?.inStorePickup ?? null;
};

export const isCurbsidePickup = (activeFeatureToggles) => {
  return activeFeatureToggles?.curbsidePickup ?? null;
};

export const isCurbsideEnabled = (enabledFeatures, market, abbrBrand) => {
  return enabledFeatures && enabledFeatures[`curbside-${market}-${abbrBrand}`];
};

export const isBopisCurbsideEnabled = (enabledFeatures, market, abbrBrand) => {
  return (
    enabledFeatures && enabledFeatures[`bopis-curbside-${market}-${abbrBrand}`]
  );
};

export const getActiveFeatureToggles = (selectedStore) => {
  return selectedStore?.activeFeatureToggles ?? null;
};

export const getSelectedStoreName = (selectedStore) => {
  return selectedStore?.storeName ?? null;
};

export const applyBopisFacet = (
  isActive,
  selectedStore,
  publish,
  bopisInteractionDataLayerBuilder,
  props
) => {
  const newSelection = {
    facetName: 'storeId',
    searchFacetOptionId: selectedStore?.storeId,
    isSelected: isActive,
  };

  publish('updateFacets', [newSelection]);

  const activeFeatureToggles = selectedStore?.activeFeatureToggles ?? null;
  const inStorePickup = isInStorePickup(activeFeatureToggles);
  const curbsidePickup = isCurbsidePickup(activeFeatureToggles);

  isActive &&
    bopisInteractionDataLayerBuilder(
      props,
      false,
      inStorePickup,
      curbsidePickup
    );
};

export const initializeBopisTealium = (
  props,
  bopisData,
  bopisInitialed,
  enabled,
  bopisTealiumInitialized,
  getBopisInitialed,
  setBopisTealiumInitialized
) => {
  const shouldInitalize = isReadyToInitialize(
    bopisInitialed,
    getBopisInitialed,
    enabled,
    bopisTealiumInitialized
  );
  if (!shouldInitalize) return;
  const selectedStore = getSelectedStore(bopisData);
  const activeFeatureToggles = getActiveFeatureToggles(selectedStore);
  const inStorePickup = isInStorePickup(activeFeatureToggles);
  const curbsidePickup = isCurbsidePickup(activeFeatureToggles);
  datalayerBuilder({
    ...props,
    onPageLoad: true,
    bopisSelected: inStorePickup,
    curbsideSelected: curbsidePickup,
  });
  setBopisTealiumInitialized(true);
};

export const storeIndicator = (curbsidePickup, inStorePickup, localize) => {
  const storePickupTextOption = storePickupTextOptions.find(
    (storePickupTextOption) =>
      storePickupTextOption.compareFn(curbsidePickup, inStorePickup)
  );
  return storePickupTextOption
    ? storePickupTextOption.getDisplayText(localize)
    : localize(BAR_LABEL_TEXT);
};

export const getBrFontWeight = (isBRFontEnabled) => {
  return isBRFontEnabled ? getFontWeight('regular') : getFontWeight('bold');
};

export const getToggleSwitchLabel = (curbsideEnabled, localize) => {
  return curbsideEnabled
    ? localize(TOGGLE_SWITCH_LABEL_CURBSIDE)
    : localize(TOGGLE_SWITCH_LABEL);
};

export const getToggleSwitchToggleInfo = (curbsideEnabled, localize) => {
  return {
    enabledText: curbsideEnabled
      ? localize(TOGGLE_ENABLED_TEXT_CURBSIDE)
      : localize(TOGGLE_ENABLED_TEXT),
    disabledText: curbsideEnabled
      ? localize(TOGGLE_DISABLED_TEXT_CURBSIDE)
      : localize(TOGGLE_DISABLED_TEXT),
  };
};
